import angular from 'angular';
import {
    MOVIMENTACAO_UNIDADE_DIAS,
    MOVIMENTACAO_UNIDADE_MONETARIA,
    MOVIMENTACAO_UNIDADES_HORAS
} from '../constants/movimentacoes-unidades.constants';

type TEventoLike = {
    codigo?: string | null;
    nome?: string | null;
    unidade?: number | string | null;
    evento_unidade?: number | string | null;
} | null | undefined;

type TFormatVigenciaOptions = {
    unicaLabel?: string;
    mensalLabel?: string;
    emptyLabel?: string;
    untilPrefix?: string;
};

type TFormatReferenciaOptions = {
    maxLength?: number;
    ellipsis?: string;
};

type TVigenciaLike = {
    vigencia: number;
    vigenciamesfinal: number | null;
    vigenciaanofinal: number | null;
};

/**
 * Limite de caracteres da referência da movimentação.
 * Compartilhado entre os formulários (entrada) e a listagem (exibição) para
 * que os dois não divirjam.
 */
export const MOVIMENTACAO_REFERENCIA_MAX_LENGTH = 20;

/**
 * Resolve a unidade configurada para o evento, aplicando fallback padrão.
 */
export function getMovimentacaoEventoUnidade(evento: TEventoLike): number {
    const unidade = evento?.unidade ?? evento?.evento_unidade;
    return Number.isFinite(Number(unidade)) ? Number(unidade) : 20;
}

/**
 * Indica se o evento usa valor baseado em horas.
 */
export function isMovimentacaoEventoHoras(evento: TEventoLike): boolean {
    return MOVIMENTACAO_UNIDADES_HORAS.indexOf(getMovimentacaoEventoUnidade(evento)) !== -1;
}

/**
 * Indica se o evento usa valor baseado em dias.
 */
export function isMovimentacaoEventoDias(evento: TEventoLike): boolean {
    return getMovimentacaoEventoUnidade(evento) === MOVIMENTACAO_UNIDADE_DIAS;
}

/**
 * Indica se o evento usa valor monetário.
 */
export function isMovimentacaoEventoMonetario(evento: TEventoLike): boolean {
    return getMovimentacaoEventoUnidade(evento) === MOVIMENTACAO_UNIDADE_MONETARIA;
}

/**
 * Indica se o evento usa valor decimal padrão.
 */
export function isMovimentacaoEventoDecimal(evento: TEventoLike): boolean {
    return !isMovimentacaoEventoHoras(evento) &&
        !isMovimentacaoEventoDias(evento) &&
        !isMovimentacaoEventoMonetario(evento);
}

/**
 * Resolve o identificador principal da entidade, com fallback para o código.
 */
export function resolveMovimentacaoEntityIdentifier(entity: any, idField: string, codeField: string): string {
    if (!entity) {
        return '';
    }

    if (typeof entity === 'string') {
        return entity.trim();
    }

    const id = typeof entity[idField] === 'string' ? entity[idField].trim() : '';
    if (id) {
        return id;
    }

    const code = typeof entity[codeField] === 'string' ? entity[codeField].trim() : '';
    return code;
}

/**
 * Normaliza o valor da movimentação conforme a unidade do evento.
 */
export function normalizeMovimentacaoValor(
    raw: string | number | null | undefined,
    evento: TEventoLike
): string | number | null {
    if (raw === null || typeof raw === 'undefined' || raw === '') {
        return null;
    }

    if (isMovimentacaoEventoHoras(evento)) {
        return formatMovimentacaoMinutesToHours(raw);
    }

    if (isMovimentacaoEventoDias(evento)) {
        const numeric = Number(`${raw}`.replace(',', '.'));
        return Number.isInteger(numeric) ? numeric : null;
    }

    const numeric = normalizeMovimentacaoDecimalValue(raw);
    if (!Number.isFinite(numeric)) {
        return null;
    }

    if (isMovimentacaoEventoDecimal(evento) && !hasAtMostTwoDecimalPlaces(numeric)) {
        return null;
    }

    return numeric;
}

/**
 * Converte um valor persistido de horas para minutos, formato esperado pelo `time-mask`.
 */
export function parseMovimentacaoHorasToMinutes(raw: string | number | null | undefined): number | null {
    if (raw === null || typeof raw === 'undefined' || raw === '') {
        return null;
    }

    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }

    const value = `${raw}`.trim();
    if (!value) {
        return null;
    }

    if (/^-?\d+$/.test(value)) {
        const minutes = Number(value);
        return Number.isFinite(minutes) ? minutes : null;
    }

    const match = value.match(/^(-?\d+):([0-5]\d)$/);
    if (!match) {
        return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null;
    }

    const absoluteMinutes = (Math.abs(hours) * 60) + minutes;
    return hours < 0 ? -absoluteMinutes : absoluteMinutes;
}

/**
 * Converte minutos do formulário para o formato de horas persistido pela API.
 */
export function formatMovimentacaoMinutesToHours(raw: string | number | null | undefined): string | null {
    const totalMinutes = parseMovimentacaoHorasToMinutes(raw);
    if (totalMinutes === null) {
        return null;
    }

    const sign = totalMinutes < 0 ? '-' : '';
    const absoluteMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const minutes = absoluteMinutes % 60;

    return `${sign}${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;
}

/**
 * Formata o número do mês com zero à esquerda para exibição.
 */
export function formatMovimentacaoMonthNumber(month: number): string {
    return month < 10 ? `0${month}` : `${month}`;
}

/**
 * Formata o valor da movimentação para leitura em UI.
 */
export function formatMovimentacaoValorLabel(
    value: string | number | null | undefined,
    evento: TEventoLike,
    $filter: angular.IFilterFunction
): string {
    if (value === null || typeof value === 'undefined' || value === '') {
        return '-';
    }

    if (!isMovimentacaoEventoMonetario(evento)) {
        return `${value}`;
    }

    const rawValue = `${value}`.trim();
    let numeric = Number(rawValue.replace(',', '.'));
    if (!Number.isFinite(numeric)) {
        numeric = Number(rawValue.replace(/\./g, '').replace(',', '.'));
    }

    if (!Number.isFinite(numeric)) {
        return `${value}`;
    }

    return $filter('currency')(numeric, 'R$ ', 2) as string;
}

/**
 * Formata a referência da movimentação, aplicando placeholder quando vazia.
 */
export function formatMovimentacaoReferenciaLabel(
    referencia: string | null | undefined,
    options: TFormatReferenciaOptions = {}
): string {
    if (!referencia || !`${referencia}`.trim()) {
        return '-';
    }

    const value = `${referencia}`.trim();
    const maxLength = typeof options.maxLength === 'number' && options.maxLength > 0
        ? options.maxLength
        : null;

    if (maxLength && value.length > maxLength) {
        return `${value.slice(0, maxLength)}${options.ellipsis || '...'}`;
    }

    return value;
}

/**
 * Formata a rubrica exibindo código e nome quando disponíveis.
 */
export function formatMovimentacaoEventoLabel(evento: TEventoLike): string {
    const codigo = evento?.codigo ? `${evento.codigo}`.trim() : '';
    const nome = evento?.nome ? `${evento.nome}`.trim() : '';

    if (codigo && nome) {
        return `${codigo} - ${nome}`;
    }

    return nome || codigo || '-';
}

/**
 * Formata a vigência da movimentação para exibição em UI.
 */
export function formatMovimentacaoVigenciaLabel(
    value: TVigenciaLike,
    options: TFormatVigenciaOptions = {}
): string {
    const {
        unicaLabel = 'Única',
        mensalLabel = 'Mensal',
        emptyLabel = '-',
        untilPrefix = 'Até'
    } = options;

    if (value.vigencia === 1) {
        return unicaLabel;
    }

    if (value.vigencia === 2) {
        return mensalLabel;
    }

    if (value.vigencia === 3 && value.vigenciamesfinal && value.vigenciaanofinal) {
        return `${untilPrefix} ${formatMovimentacaoMonthNumber(value.vigenciamesfinal)}/${value.vigenciaanofinal}`;
    }

    return emptyLabel;
}

/**
 * Normaliza entradas numéricas ou textuais da UI para decimal comum,
 * aceitando ponto ou vírgula como separador decimal.
 */
function normalizeMovimentacaoDecimalValue(raw: string | number): number {
    if (typeof raw === 'number') {
        return raw;
    }

    const text = `${raw}`.trim();
    const hasComma = text.indexOf(',') !== -1;
    const hasDot = text.indexOf('.') !== -1;
    const normalizedText = hasComma && hasDot
        ? text.replace(/\./g, '').replace(',', '.')
        : hasComma
            ? text.replace(',', '.')
            : text;

    return Number(normalizedText);
}

function hasAtMostTwoDecimalPlaces(value: number): boolean {
    const scaled = value * 100;
    return Math.abs(scaled - Math.round(scaled)) < Number.EPSILON;
}
